// The ported modules' templates, module.<kind> (contract §4.8.1, §8.3; Mike,
// §18.1 question 1): every overview module kind on Gallivant's boards but the
// anchor watch is a static page drawn as today's module body
// (cloud/web/src/components/dashboard/overview: modules.tsx,
// OutlookModules.tsx, UnderwayModules.tsx), every figure through its one
// module feed's fields and rows. The card around it is the module card, whose
// title, chips, note, footer and freshness line are the feed's (§6.2).
//
// Shared here: the feed and its card, the attribute makers, and the CSS every
// module body uses (the readout, the module's own message line, the model's
// honesty line). A host-filled element is a leaf (the host writes its text),
// so a drawing turned by a figure is a `<use>` of a shape in `<defs>`: the
// use takes the figure's `--frac`, and the shape inherits it.
//
// A module page in a mode board's slot is laid out for it (§5.6): the host
// sets `data-slot` on its `<html>` (the bento's cell by kind, or the underway
// ladder's `strip`, `hero`, `wide`, `flex-1` to `flex-3`), and each template
// restyles itself there as dashboard.css restyles today's module in that cell.
//
// The Marina look (cloud ADR 0396, TOM-678): each module takes its kind
// (`MODULE_DOMAINS`, as TOM-679's module cards draw it) on its root, so its
// lanes, marks and bars draw in the kind's colour; a module of no kind (the
// traffic) keeps ink and the coral accent. A module whose body draws a lane
// (`lane`) gets the page's one area gradient and the range chip's CSS, and
// one whose CSS reads the kind's colours (`kind`) their rules.

import {
  AREA_CSS,
  CHIP_CSS,
  areaGradient,
  css,
  domainCss,
  domainOf,
  h,
  output,
} from "./kit.js";
import { pageDomainForModule } from "./domains.js";
import { MODULE_CSS } from "./module-kit.js";
import { moduleForecast } from "./module-forecast.js";
import { moduleTide } from "./module-tide.js";
import { modulePowerBudget } from "./module-power-budget.js";
import { moduleEnvironmentOutlook } from "./module-environment-outlook.js";
import { moduleTanksBudget } from "./module-tanks-budget.js";
import { modulePressure } from "./module-pressure.js";
import { moduleDepthSafety } from "./module-depth-safety.js";
import { moduleHelm } from "./module-helm.js";
import { moduleEngineVitals } from "./module-engine-vitals.js";
import { moduleTraffic } from "./module-traffic.js";
import { moduleReserves } from "./module-reserves.js";

const BUILDERS = {
  "depth-safety": moduleDepthSafety,
  "engine-vitals": moduleEngineVitals,
  "environment-outlook": moduleEnvironmentOutlook,
  forecast: moduleForecast,
  helm: moduleHelm,
  "power-budget": modulePowerBudget,
  pressure: modulePressure,
  reserves: moduleReserves,
  "tanks-budget": moduleTanksBudget,
  tide: moduleTide,
  traffic: moduleTraffic,
};

/**
 * The kinds the anchored and docked bento places, each in the cell named by
 * its kind (§5.6's `data-slot`). The bento's readouts are denser than the
 * library's (dashboard.css `--ov-*`): the state of charge 68 px, a large
 * readout 44 px.
 */
const BENTO = new Set([
  "environment-outlook",
  "forecast",
  "power-budget",
  "pressure",
  "tanks-budget",
  "tide",
]);

/** A lane module's gradient holder and its range chip's row. */
const LANE_CSS = css(`
  .mo-defs { position: absolute; width: 0; height: 0; overflow: hidden; }
  .mo-rc { display: flex; justify-content: flex-end; flex: none; margin-top: 6px; }
`);

/** The module kinds a template ports (the protocol's NEO_PAGE_MODULE_PORTS), sorted. */
export const MODULE_TEMPLATE_PORTS = Object.freeze(
  Object.keys(BUILDERS).sort(),
);

/** module.<kind> for a ported kind (§8.3). */
export function moduleTemplate(source, context) {
  const spec = source.spec;
  const body = BUILDERS[spec.module](spec, context);
  const feedSpec = {
    v: 1,
    kind: "module",
    boatId: spec.boatId,
    module: spec.module,
    mode: spec.mode,
    config: spec.config ?? {},
  };
  const domain = pageDomainForModule(spec.module);
  const lane = body.lane === true;
  return output(`module.${spec.module}`, 1, {
    html: h(
      "div",
      { class: `tp mo mo-${spec.module}`, ...domainOf(domain) },
      lane
        ? h(
            "svg",
            { class: "mo-defs", "aria-hidden": "true" },
            h("defs", {}, areaGradient("mo-a")),
          )
        : "",
      body.html,
    ),
    css:
      MODULE_CSS +
      (lane ? CHIP_CSS + AREA_CSS + LANE_CSS : "") +
      body.css +
      (body.kind === true
        ? domainCss([domain, ...(body.domains ?? [])].filter(Boolean), {
            icon: body.icon === true,
            base: domain === null,
          })
        : "") +
      (BENTO.has(spec.module)
        ? css(`
            html[data-slot=${spec.module}] .rd-xl { font-size: 68px; }
            html[data-slot=${spec.module}] .rd-lg { font-size: 44px; }
          `)
        : ""),
    feeds: [{ id: "module", kind: "module", spec: feedSpec }],
    card: { kind: "module", feed: "module" },
  });
}
